import { Trans, t } from "@lingui/macro";
import { memo, useCallback, useState } from "react";
import { useMedia } from "react-use";

import { useAuth } from "context/AuthContext/AuthProvider";
import { useIsPositionsLoading, usePositionsInfoData } from "context/SyntheticsStateContext/hooks/globalsHooks";
import { usePositionEditorPositionState } from "context/SyntheticsStateContext/hooks/positionEditorHooks";
import { selectAccount, selectChainId } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { selectPositionsInfoDataSortedByMarket } from "context/SyntheticsStateContext/selectors/positionsSelectors";
import { selectShowPnlAfterFees } from "context/SyntheticsStateContext/selectors/settingsSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { PositionInfo } from "domain/synthetics/positions";
import { addRemovedPositionKey } from "domain/synthetics/positions/removedPositions";
import { TradeMode } from "domain/synthetics/trade";
import { requestTradeDataRefresh } from "domain/synthetics/tradeDataRefreshEvents";
import { removeAccountPosition } from "lib/apis/accounts";
import { helperToast } from "lib/helperToast";
import { getByKey } from "lib/objects";
import { userAnalytics } from "lib/userAnalytics";
import { SharePositionClickEvent } from "lib/userAnalytics/types";

import Button from "components/Button/Button";
import PositionShare from "components/Exchange/PositionShare";
import Modal from "components/Modal/Modal";
import { OrderEditorContainer } from "components/OrderEditorContainer/OrderEditorContainer";
import { PositionItem } from "components/Synthetics/PositionItem/PositionItem";
import { PositionTradeDetailsModal } from "components/Synthetics/PositionTradeDetailsModal";
import { Table, TableTd, TableTh, TableTheadTr, TableTr } from "components/Table/Table";

import "./PositionList.scss";

type Props = {
  onSelectPositionClick: (key: string, tradeMode?: TradeMode, showCurtain?: boolean) => void;
  onClosePositionClick: (key: string) => void;
  onOrdersClick: (positionKey: string, orderKey: string | undefined) => void;
  onCancelOrder: (key: string) => void;
  openSettings: () => void;
  hideActions?: boolean;
};

export function PositionList(p: Props) {
  const { onClosePositionClick, onOrdersClick, onSelectPositionClick, openSettings, onCancelOrder, hideActions } = p;
  const positionsInfoData = usePositionsInfoData();
  const chainId = useSelector(selectChainId);
  const account = useSelector(selectAccount);
  const { isAuthenticated, signIn } = useAuth();
  const [isPositionShareModalOpen, setIsPositionShareModalOpen] = useState(false);
  const [positionToShareKey, setPositionToShareKey] = useState<string>();
  const [positionTradeDetailsKey, setPositionTradeDetailsKey] = useState<string>();
  const [positionToRemoveKey, setPositionToRemoveKey] = useState<string>();
  const [isRemovingPosition, setIsRemovingPosition] = useState(false);
  const positionToShare = getByKey(positionsInfoData, positionToShareKey);
  const positionForTradeDetails = getByKey(positionsInfoData, positionTradeDetailsKey);
  const positions = useSelector(selectPositionsInfoDataSortedByMarket);
  const positionToRemove = positionToRemoveKey ? positions.find((position) => position.key === positionToRemoveKey) : undefined;

  const handleSharePositionClick = useCallback((positionKey: string) => {
    userAnalytics.pushEvent<SharePositionClickEvent>({
      event: "SharePositionAction",
      data: {
        action: "SharePositionClick",
      },
    });
    setPositionToShareKey(positionKey);
    setIsPositionShareModalOpen(true);
  }, []);

  const handleRemovePositionClick = useCallback((positionKey: string) => {
    setPositionToRemoveKey(positionKey);
  }, []);

  const handleConfirmRemovePosition = useCallback(async () => {
    if (!account || !positionToRemoveKey || !positionToRemove) return;

    setIsRemovingPosition(true);
    try {
      if (!isAuthenticated) {
        const session = await signIn();
        if (!session) {
          helperToast.error(t`Sign in required to remove this position.`);
          return;
        }
      }

      await removeAccountPosition(account, {
        chainId,
        positionKey: positionToRemove.contractKey,
        market: positionToRemove.marketAddress,
        collateralToken: positionToRemove.collateralTokenAddress,
        isLong: positionToRemove.isLong,
      });
      addRemovedPositionKey(chainId, account, positionToRemoveKey);
      requestTradeDataRefresh("position-removal");
      setPositionToRemoveKey(undefined);
    } finally {
      setIsRemovingPosition(false);
    }
  }, [account, chainId, isAuthenticated, positionToRemove, positionToRemoveKey, signIn]);

  const [, setEditingPositionKey] = usePositionEditorPositionState();
  const isLoading = useIsPositionsLoading();
  const isMobile = useMedia("(max-width: 1300px)");

  return (
    <div className="rounded-8 bg-gray-darker px-16">
      {isMobile && (
        <>
          {positions.length === 0 && (
            <div className="App-card text-gray-lighter">
              {isLoading ? t`Loading...` : account ? t`No open positions` : t`Connect Wallet`}
            </div>
          )}
          <div className="grid grid-cols-1 gap-8 min-[800px]:grid-cols-2">
            {!isLoading &&
              positions.map((position) => (
                <PositionItemWrapper
                  key={position.key}
                  position={position}
                  onEditCollateralClick={setEditingPositionKey}
                  onClosePositionClick={onClosePositionClick}
                  onOrdersClick={onOrdersClick}
                  onSelectPositionClick={onSelectPositionClick}
                  isLarge={false}
                  onShareClick={handleSharePositionClick}
                  onRemovePositionClick={handleRemovePositionClick}
                  onTradeDetailsClick={setPositionTradeDetailsKey}
                  openSettings={openSettings}
                  hideActions={hideActions}
                  onCancelOrder={onCancelOrder}
                />
              ))}
          </div>
        </>
      )}

      {!isMobile && (
        <Table>
          <thead className="text-body-medium">
            <TableTheadTr className="border-b border-gray-light">
              <TableTh>
                <Trans>Position</Trans>
              </TableTh>
              <TableTh>
                <Trans>Size</Trans>
              </TableTh>
              <TableTh>
                <Trans>Net Value</Trans>
              </TableTh>
              <TableTh>
                <Trans>Collateral</Trans>
              </TableTh>
              <TableTh>
                <Trans>Entry Price</Trans>
              </TableTh>
              <TableTh>
                <Trans>Market Price</Trans>
              </TableTh>
              <TableTh>
                <Trans>Liq. Price</Trans>
              </TableTh>
              {!isLoading && !p.hideActions && (
                <>
                  <TableTh></TableTh>
                  <TableTh></TableTh>
                </>
              )}
            </TableTheadTr>
          </thead>
          <tbody>
            {positions.length === 0 && (
              <TableTr hoverable={false} bordered={false}>
                <TableTd colSpan={15}>
                  <div className="flex flex-col items-center justify-center py-40">
                    <span className="text-14 font-medium text-white">
                      {isLoading ? t`Loading...` : account ? t`No trades yet` : t`Connect Wallet`}
                    </span>
                    {!isLoading && account && (
                      <span className="text-12 text-[#9D9D9D]">{t`Initiate a trade to see your open positions`}</span>
                    )}
                    {!isLoading && !account && (
                      <span className="text-12 text-[#9D9D9D]">{t`Connect your wallet to see your open positions`}</span>
                    )}
                  </div>
                </TableTd>
              </TableTr>
            )}
            {!isLoading &&
              positions.map((position) => (
                <PositionItemWrapper
                  key={position.key}
                  position={position}
                  onEditCollateralClick={setEditingPositionKey}
                  onClosePositionClick={onClosePositionClick}
                  onOrdersClick={onOrdersClick}
                  onSelectPositionClick={onSelectPositionClick}
                  isLarge
                  onShareClick={handleSharePositionClick}
                  onRemovePositionClick={handleRemovePositionClick}
                  onTradeDetailsClick={setPositionTradeDetailsKey}
                  openSettings={openSettings}
                  hideActions={hideActions}
                  onCancelOrder={onCancelOrder}
                />
              ))}
          </tbody>
        </Table>
      )}
      {positionToShare && (
        <PositionShare
          key={positionToShare.key}
          setIsPositionShareModalOpen={setIsPositionShareModalOpen}
          isPositionShareModalOpen={isPositionShareModalOpen}
          entryPrice={positionToShare.entryPrice}
          indexToken={positionToShare.indexToken}
          isLong={positionToShare.isLong}
          leverage={positionToShare.leverageWithPnl}
          markPrice={positionToShare.markPrice}
          pnlAfterFeesUsd={positionToShare.pnlAfterFees}
          pnlAfterFeesPercentage={positionToShare?.pnlAfterFeesPercentage}
          chainId={chainId}
          account={account}
        />
      )}
      <PositionTradeDetailsModal
        isVisible={Boolean(positionForTradeDetails)}
        onClose={() => setPositionTradeDetailsKey(undefined)}
        position={positionForTradeDetails}
      />
      <Modal
        isVisible={Boolean(positionToRemoveKey)}
        setIsVisible={(isVisible) => {
          if (!isVisible) setPositionToRemoveKey(undefined);
        }}
        label={t`Remove position?`}
        className="PositionRemoveModal"
        footerContent={
          <div className="PositionRemoveModal-actions">
            <Button variant="tertiary" onClick={() => setPositionToRemoveKey(undefined)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button variant="primary-action" onClick={handleConfirmRemovePosition} disabled={isRemovingPosition}>
              <Trans>Remove</Trans>
            </Button>
          </div>
        }
      >
        <div className="PositionRemoveModal-body">
          <p>
            <Trans>This removes the position from this list.</Trans>
          </p>
          <p>
            <Trans>Live on-chain positions must still be closed with Close.</Trans>
          </p>
          <p>
            <Trans>This action can't be reversed.</Trans>
          </p>
          {positionToRemove && (
            <div className="PositionRemoveModal-summary">
              <span>{positionToRemove.indexName}</span>
              <span>{positionToRemove.isLong ? t`Long` : t`Short`}</span>
            </div>
          )}
        </div>
      </Modal>
      <OrderEditorContainer />
    </div>
  );
}

const PositionItemWrapper = memo(
  ({
    position,
    hideActions,
    isLarge,
    onClosePositionClick,
    onEditCollateralClick,
    onOrdersClick,
    onSelectPositionClick,
    onShareClick,
    onRemovePositionClick,
    onTradeDetailsClick,
    openSettings,
    onCancelOrder,
  }: {
    position: PositionInfo;
    onEditCollateralClick: (positionKey: string) => void;
    onClosePositionClick: (positionKey: string) => void;
    onOrdersClick: (positionKey: string, orderKey: string | undefined) => void;
    onSelectPositionClick: (positionKey: string, tradeMode: TradeMode | undefined, showCurtain?: boolean) => void;
    isLarge: boolean;
    onShareClick: (positionKey: string) => void;
    onRemovePositionClick: (positionKey: string) => void;
    onTradeDetailsClick: (positionKey: string) => void;
    openSettings: () => void;
    hideActions: boolean | undefined;
    onCancelOrder: (orderKey: string) => void;
  }) => {
    const showPnlAfterFees = useSelector(selectShowPnlAfterFees);
    const handleEditCollateralClick = useCallback(
      () => onEditCollateralClick(position.key),
      [onEditCollateralClick, position.key]
    );
    const handleClosePositionClick = useCallback(
      () => onClosePositionClick(position.key),
      [onClosePositionClick, position.key]
    );

    const handleSelectPositionClick = useCallback(
      (tradeMode?: TradeMode, showCurtain?: boolean) => onSelectPositionClick(position.key, tradeMode, showCurtain),
      [onSelectPositionClick, position.key]
    );
    const handleShareClick = useCallback(() => onShareClick(position.key), [onShareClick, position.key]);
    const handleRemovePositionClick = useCallback(
      () => onRemovePositionClick(position.key),
      [onRemovePositionClick, position.key]
    );
    const handleTradeDetailsClick = useCallback(
      () => onTradeDetailsClick(position.key),
      [onTradeDetailsClick, position.key]
    );
    const handleCancelOrder = useCallback((orderKey: string) => onCancelOrder(orderKey), [onCancelOrder]);
    const handleOrdersClick = useCallback(
      (orderKey: string | undefined) => {
        onOrdersClick(position.key, orderKey);
      },
      [onOrdersClick, position.key]
    );

    return (
      <PositionItem
        position={position}
        onEditCollateralClick={handleEditCollateralClick}
        onClosePositionClick={handleClosePositionClick}
        onOrdersClick={handleOrdersClick}
        onSelectPositionClick={handleSelectPositionClick}
        showPnlAfterFees={showPnlAfterFees}
        isLarge={isLarge}
        openSettings={openSettings}
        hideActions={hideActions}
        onShareClick={handleShareClick}
        onRemovePositionClick={handleRemovePositionClick}
        onTradeDetailsClick={handleTradeDetailsClick}
        onCancelOrder={handleCancelOrder}
      />
    );
  }
);
